import React, { useEffect } from "react";
import { PrefillSelect } from "@components/input";
import { useSelector, useDispatch } from "react-redux";
import { useFormikContext } from "formik";
import { isNotNullOrEmpty, stringIsNotNullOrEmpty } from "@utils/helpers/stringHelper";
import { createOption } from "../../../../constants/options";
import { FIELD_NAMES, LOAN_REASONS } from "../../../../constants/commonConstants";
import { useRequestedProductType } from "@hooks";
import actions from "@redux/loanForm/actions";

// given the currently selected requested product type, supply a list of loan reasons that it would be valid for
const useLoanReasonOptions = () => {
  let loanReasonOptions = [];
  const { loanReasonsResponse } = useSelector((state) => state.StartUp);
  const requestedProductType = useRequestedProductType();
  
  if (isNotNullOrEmpty(loanReasonsResponse)) {
    loanReasonsResponse.forEach((property) => {
      if (property.type === requestedProductType) {
        loanReasonOptions = property.loanReasons;
      }
    })
  }

  return loanReasonOptions;
};

// each time the possible loanReasons change we need to ensure the currently selected loan reason doesnt need to be reset
// as if the newly selected requested product type updates our loanReasonOptions to no longer include our loan type we 
// must now enter a new loan type
const useHandleInvalidLoanReasons = () => {
  const loanReasonsIsFetching = useLoanReasonsIsFetchingSelector();
  
  const loanReasonOptions = useLoanReasonOptions();
  const {setFieldValue, values: { [FIELD_NAMES.LOAN_REASON]: value }} = useFormikContext();
  
  useEffect(() => {
      const loanReasonsValues = loanReasonOptions.map(({value: optionValue}) => optionValue);
      const resetField = () => setFieldValue(FIELD_NAMES.LOAN_REASON, "");
      // we don't reset until we are done fetching as we cannot know if the value is valid until after then
      const fieldContainsInvalidLoanReason = !loanReasonsIsFetching && isNotNullOrEmpty(value) && !loanReasonsValues.includes(value);
      
      if (fieldContainsInvalidLoanReason) {
        resetField();
      }
  }, [loanReasonOptions, loanReasonsIsFetching, value, setFieldValue]);
};

const useLoanReasonsIsFetchingSelector = () => {
  const {loanReasonsIsFetching} = useSelector((state) => state.StartUp);

  return loanReasonsIsFetching;
};

const previewMapper = (value) => {
  if (value === LOAN_REASONS.VEHICLE_PURCHASE) {
    return "Purchase a vehicle";
  }
  
  return value;
}

const LoanReasons = () => {
  const { theme } = useSelector((state) => state.StartUp);
  const dispatch = useDispatch();
  const loanReasonsIsFetching = useLoanReasonsIsFetchingSelector();
  const loanReasonOptions = useLoanReasonOptions();
  const requestedProductType = useRequestedProductType();
  useHandleInvalidLoanReasons();

  const hasSelectedProductType = stringIsNotNullOrEmpty(requestedProductType);
  const options = hasSelectedProductType ? loanReasonOptions : [createOption("Please Select Loan Type", "")];
  const placeholder = hasSelectedProductType ? "Select Reason of Loan" : undefined;

  useEffect(()=> {
    if (!loanReasonsIsFetching && theme === "rentBondTheme") {
      dispatch(actions.setFieldInitialValue({[FIELD_NAMES.LOAN_REASON]: "Bond Loan"}));
    }
  }, [theme, loanReasonsIsFetching, dispatch]);

  return (
    <PrefillSelect
      name={FIELD_NAMES.LOAN_REASON}
      title="Reason for loan"
      loading={loanReasonsIsFetching}
      options={options}
      previewMapper={previewMapper}
      toolTipText="What do you require the majority of the funds for?"
      placeholder={placeholder}
    />
  );
}

export default LoanReasons;